import { builtInVariants } from '@ce/theme';
import { CONFIG_SCHEMA, type AppConfig, type NavigationConfig, type PageDef, type TabDef, type WidgetInstance } from './types';
import { makeWidget } from './registry';
import type { WidgetType } from './types';

/**
 * Defaults and presets.
 *
 * `defaultAppConfig()` reproduces the app exactly as it shipped before the
 * admin existed — same six tabs, same Home, same order. That is a requirement,
 * not a convenience: with no config published, or a config that fails
 * validation, an attendee must see the app they already know. Anything else
 * would make publishing the admin a breaking change for every deployment.
 */

/** A widget with fixed id and overridden props — presets need stable ids. */
function w(type: WidgetType, id: string, props: Record<string, unknown> = {}, extra: Partial<WidgetInstance> = {}): WidgetInstance {
  const base = makeWidget(type, id);
  return { ...base, props: { ...base.props, ...props }, ...extra };
}

function tab(id: string, label: string, icon: string, target: TabDef['target'], enabled = true): TabDef {
  return { id, label, icon, target, enabled };
}

const HEADER_ALL = { search: true, inbox: true, cart: true, grid: true };

const FEATURES_ALL = {
  journey: true, tickets: true, shop: true, themePicker: true, callSheet: true, askAssistant: true,
};

const EFFECTS_ALL = { ambient: true, entrance: true, rotatorMotion: true };

const CLASSIC_TABS: TabDef[] = [
  tab('t-today', 'Today', 'today', { kind: 'page', pageId: 'home' }),
  tab('t-schedule', 'Schedule', 'schedule', { kind: 'screen', screen: 'schedule' }),
  tab('t-talent', 'Talent', 'talent', { kind: 'screen', screen: 'talent' }),
  tab('t-hall', 'Hall', 'hall', { kind: 'screen', screen: 'hall' }),
  tab('t-quests', 'Quests', 'quests', { kind: 'screen', screen: 'quests' }),
  tab('t-me', 'Me', 'me', { kind: 'screen', screen: 'me' }),
];

function classicHome(): PageDef {
  return {
    id: 'home',
    title: 'Your day',
    kicker: '{dayLong}',
    widgets: [
      w('dayPicker', 'w-daypicker'),
      w('upNext', 'w-upnext'),
      w('promoRotator', 'w-promos'),
      w('quickLinks', 'w-quicklinks'),
      w('journeyCta', 'w-journeycta'),
      w('callSheet', 'w-callsheet'),
      w('featuredTalent', 'w-featured'),
      w('specialEvents', 'w-special'),
    ],
  };
}

export function defaultAppConfig(tenantId = 'nocturne-con'): AppConfig {
  return {
    schema: CONFIG_SCHEMA,
    tenantId,
    revision: 'default',
    theme: {},
    themeVariants: builtInVariants.map((v) => ({ ...v })),
    navigation: {
      style: 'bottom',
      showLabels: true,
      tabs: CLASSIC_TABS.map((t) => ({ ...t, target: { ...t.target } as TabDef['target'] })),
      header: { ...HEADER_ALL },
    },
    pages: { home: classicHome() },
    features: { ...FEATURES_ALL },
    effects: { ...EFFECTS_ALL },
  };
}

export function defaultNavigation(): NavigationConfig {
  return defaultAppConfig().navigation;
}

// ── Presets ─────────────────────────────────────────────────────────────────

export interface LayoutPreset {
  id: string;
  label: string;
  /** One line on who it is for. */
  description: string;
  /** Names the navigation pattern, for the preset card. */
  pattern: string;
  build: () => Pick<AppConfig, 'navigation' | 'pages' | 'features'>;
}

/**
 * Presets replace navigation, pages and feature flags — the structural half of
 * a config. They deliberately leave theme, variants and effects alone: switching
 * from a tab bar to a drawer should not repaint the brand.
 */
export const PRESETS: LayoutPreset[] = [
  {
    id: 'classic',
    label: 'Classic',
    pattern: 'Bottom tabs',
    description: 'The familiar six-tab layout. A safe starting point for any event.',
    build: () => {
      const c = defaultAppConfig();
      return { navigation: c.navigation, pages: c.pages, features: c.features };
    },
  },
  {
    id: 'live',
    label: 'Live event',
    pattern: 'Floating dock',
    description: 'Built for the weekend itself: countdown, polls, feedback and suggestions front and centre.',
    build: () => ({
      navigation: {
        style: 'floating',
        showLabels: true,
        header: { ...HEADER_ALL },
        tabs: [
          tab('t-today', 'Today', 'today', { kind: 'page', pageId: 'home' }),
          tab('t-schedule', 'Schedule', 'schedule', { kind: 'screen', screen: 'schedule' }),
          tab('t-live', 'Live', 'live', { kind: 'page', pageId: 'live' }),
          tab('t-hall', 'Hall', 'hall', { kind: 'screen', screen: 'hall' }),
          tab('t-me', 'Me', 'me', { kind: 'screen', screen: 'me' }),
        ],
      },
      features: { ...FEATURES_ALL },
      pages: {
        home: {
          id: 'home', title: 'Your day', kicker: '{dayLong}',
          widgets: [
            w('banner', 'w-welcome', { tone: 'highlight', title: 'Welcome to the weekend', body: 'Doors open at 09:00. Photo op studios open at 10:00.' }),
            w('dayPicker', 'w-daypicker'),
            w('upNext', 'w-upnext'),
            w('smartSuggestions', 'w-suggest'),
            w('promoRotator', 'w-promos'),
            w('quickLinks', 'w-quicklinks'),
            w('featuredTalent', 'w-featured'),
          ],
        },
        live: {
          id: 'live', title: 'Live', kicker: 'Happening now',
          widgets: [
            w('poll', 'w-poll'),
            w('liveFeedback', 'w-feedback'),
            w('quizCard', 'w-quiz'),
            w('sessionList', 'w-coming', { title: 'Coming up' }),
            w('smartSuggestions', 'w-suggest-live', { title: 'You might have missed', strategy: 'behavior' }),
          ],
        },
      },
    }),
  },
  {
    id: 'hub',
    label: 'Fan hub',
    pattern: 'Side drawer',
    description: 'A dashboard home with commerce up front. Suits events that sell more than they schedule.',
    build: () => ({
      navigation: {
        style: 'drawer',
        showLabels: true,
        header: { ...HEADER_ALL },
        tabs: [
          tab('t-home', 'Home', 'today', { kind: 'page', pageId: 'home' }),
          tab('t-schedule', 'Schedule', 'schedule', { kind: 'screen', screen: 'schedule' }),
          tab('t-talent', 'Talent', 'talent', { kind: 'screen', screen: 'talent' }),
          tab('t-hall', 'Hall & map', 'map', { kind: 'screen', screen: 'hall' }),
          tab('t-shop', 'Shop', 'shop', { kind: 'screen', screen: 'shop' }),
          tab('t-tickets', 'Admission', 'ticket', { kind: 'screen', screen: 'tickets' }),
          tab('t-quests', 'Quests', 'star', { kind: 'screen', screen: 'quests' }),
          tab('t-inbox', 'Inbox', 'bell', { kind: 'screen', screen: 'messages' }),
          tab('t-me', 'My badge', 'me', { kind: 'screen', screen: 'me' }),
        ],
      },
      features: { ...FEATURES_ALL },
      pages: {
        home: {
          id: 'home', title: 'Welcome', kicker: '{event}',
          widgets: [
            w('ticketCta', 'w-tickets'),
            w('promoRotator', 'w-promos'),
            w('quickLinks', 'w-quicklinks'),
            w('merchStrip', 'w-merch'),
            w('featuredTalent', 'w-featured', { mode: 'all' }),
            w('specialEvents', 'w-special', { title: 'Special events' }),
          ],
        },
      },
    }),
  },
  {
    id: 'minimal',
    label: 'Programme only',
    pattern: 'Top tabs',
    description: 'Schedule, talent and the hall — nothing to buy, nothing to configure. For free or invite-only events.',
    build: () => ({
      navigation: {
        style: 'top',
        showLabels: true,
        header: { search: true, inbox: false, cart: false, grid: true },
        tabs: [
          tab('t-today', 'Today', 'today', { kind: 'page', pageId: 'home' }),
          tab('t-schedule', 'Schedule', 'schedule', { kind: 'screen', screen: 'schedule' }),
          tab('t-talent', 'Talent', 'talent', { kind: 'screen', screen: 'talent' }),
          tab('t-hall', 'Hall', 'hall', { kind: 'screen', screen: 'hall' }),
        ],
      },
      features: { journey: false, tickets: false, shop: false, themePicker: false, callSheet: true, askAssistant: true },
      pages: {
        home: {
          id: 'home', title: 'Today', kicker: '{dayLong}',
          widgets: [
            w('dayPicker', 'w-daypicker'),
            w('upNext', 'w-upnext'),
            w('sessionList', 'w-list', { title: 'On today', limit: 6 }),
            w('richText', 'w-info', { heading: 'Good to know', body: 'Add practical information here \u2014 parking, accessibility, quiet spaces.' }),
          ],
        },
      },
    }),
  },
];

export function findPreset(id: string): LayoutPreset | undefined {
  return PRESETS.find((p) => p.id === id);
}
